<script setup>
import {
  ref,
  computed,
  watch,
  toRefs,
} from 'vue';
import { utilsUsuarios } from 'cifra-api-js';
import { orderBy } from 'lodash';
import { precio } from '../../../../../libs/cifra/importes';
import { cantidadDisplay } from '../../../../../libs/cifra/descuentos';
import { dateToTimeString, dateToLocaleDateString } from '../../../../../libs/dates';
import suscripciones from '../../../../../api/cifra/suscripciones';
import Overlay from '../../../../../libs/cifra/overlay';
import { useGeneracionSuscripciones } from './GeneracionMixin';
import SelectorNumerico from '../../../../../components/inputs/SelectorNumerico.vue';
import InputCheckboxBoolean from '../../../../../components/cifra/InputCheckboxBoolean.vue';

// Props
const props = defineProps({
  infoServicio: {
    type: Object,
    required: true,
  },
  suscriptores: {
    type: Object,
    required: true,
  },
});

// Template refs
const tablaSuscriptores = ref();

// Make props reactive
const { infoServicio, suscriptores } = toRefs(props);

// Reactive data
const cantidadAsignable = ref(false);
const campoOrdenacion = ref('nombreCompleto');
const direccionOrdenacion = ref('asc');

// Use composable for GeneracionMixin functionality
const {
  suscriptoresValores,
  numeroSuscriptores,
  esTransporte,
  esTransporteEventuales,
  nombreGrupo,
} = useGeneracionSuscripciones(infoServicio, suscriptores);

// Component computed properties
const suscriptoresOdenados = computed(() => {
  if (campoOrdenacion.value === 'nombreCompleto') {
    const ordenados = utilsUsuarios.ordenar(suscriptoresValores.value);
    if (direccionOrdenacion.value === 'desc') {
      return ordenados.toReversed(); // ECMA 2023
    }
    return ordenados;
  }
  return orderBy(suscriptoresValores.value, campoOrdenacion.value, direccionOrdenacion.value);
});

const totalImporteSuscriptores = computed(() => precio(
  suscriptoresValores.value.map((sus) => sus.importe_final).reduce((a, b) => a + b, 0),
));

const keysSuscriptores = computed(() => Object.keys(suscriptores.value));

const descuentoDisplay = (descuento) => cantidadDisplay(descuento);

// Declare methods that are used by other methods first
const recalcularSuscriptor = (suscriptor) => {
  // Si han puesto el importe manual, no hay que recalcular nunca
  if (suscriptor.importe_manual) {
    return;
  }

  if (!suscriptor.numero_servicios) {
    suscriptor.numero_servicios = 1;
  }

  suscripciones.infoSuscripcion(
    suscriptor.id,
    infoServicio.value.modalidad_id,
    { params: { numero_servicios: suscriptor.numero_servicios } },
  ).then((result) => {
    const suscripcion = result.data;
    suscriptor.importe_final = parseFloat(suscripcion.importe_final);
    suscriptor.descuentos = suscripcion.servicio_alumno_descuentos;
  });
};

const actualizarNumeroServiciosEventuales = (suscriptor) => {
  const seleccionados = suscriptor.transporte_eventuales.filter(
    (eventual) => eventual.seleccionada,
  ).length;
  suscriptor.numero_servicios = seleccionados;
  recalcularSuscriptor(suscriptor);
};

// Component methods
const eliminarSuscriptor = (idSuscriptor) => {
  const suscriptor = suscriptores.value[idSuscriptor];
  suscriptor.importe_manual = false;
  suscriptor.importe_final = 0;
  delete suscriptores.value[idSuscriptor];
};

const ordenar = (s) => {
  if (s === campoOrdenacion.value) {
    direccionOrdenacion.value = direccionOrdenacion.value === 'asc' ? 'desc' : 'asc';
  } else {
    direccionOrdenacion.value = 'asc';
  }
  campoOrdenacion.value = s;
};

const cambiarImporteManual = (suscriptor) => {
  suscriptor.importe_manual = !suscriptor.importe_manual;
  if (!suscriptor.importe_manual) {
    recalcularSuscriptor(suscriptor);
  }
};

const marcarTodoTransporte = () => {
  const inscripciones = [];
  const eventuales = [];
  const suscriptoresRecalcular = [];
  let algunaSinMarcar = false;

  suscriptoresValores.value.forEach((suscriptor) => {
    if (suscriptor.transporte_inscripciones) {
      suscriptor.transporte_inscripciones.forEach((inscripcion) => {
        if (!inscripcion['suscripcion?']) {
          if (!inscripcion.seleccionada) {
            algunaSinMarcar = true;
          }
          inscripciones.push(inscripcion);
        }
      });
    }
    if (suscriptor.transporte_eventuales && suscriptor.transporte_eventuales.length) {
      suscriptor.transporte_eventuales.forEach((eventual) => {
        if (!eventual['suscripcion?']) {
          if (!eventual.seleccionada) {
            algunaSinMarcar = true;
          }
          eventuales.push(eventual);
        }
      });
      // Si tiene eventuales hay que recalcular porque el precio cambia dependiendo
      // de los días que haya ido
      suscriptoresRecalcular.push(suscriptor);
    }
  });

  // Si hay alguna sin marcar, marcamos, si no desmarcamos
  inscripciones.forEach((inscripcion) => { inscripcion.seleccionada = algunaSinMarcar; });
  eventuales.forEach((eventual) => { eventual.seleccionada = algunaSinMarcar; });
  suscriptoresRecalcular.forEach((sus) => actualizarNumeroServiciosEventuales(sus));
};

const recalcularSuscriptores = (suscriptoresParam) => {
  // Nos aseguramos de que tenga el número de servicios asignado
  suscriptoresParam.forEach((sus) => {
    if (!sus.numero_servicios) {
      sus.numero_servicios = 1;
    }
  });

  const idsUno = suscriptoresParam.filter((sus) => sus.numero_servicios === 1).map((sus) => sus.id);
  if (idsUno.length) {
    const overlay = new Overlay(tablaSuscriptores.value);
    overlay.show();
    suscripciones.infoSuscripciones(
      idsUno, infoServicio.value.modalidad_id,
    ).then((result) => {
      result.data.forEach((suscripcion) => {
        const suscriptor = suscriptores.value[suscripcion.alumno_id];
        if (!suscriptor.importe_manual) {
          suscriptor.importe_final = parseFloat(suscripcion.importe_final);
          suscriptor.descuentos = suscripcion.servicio_alumno_descuentos;
        }
      });
    }).finally(() => {
      overlay.hide();
    });
  }
  // Los que tienen más de una cantidad hay que preguntarlos a parte
  suscriptoresParam.filter((sus) => sus.numero_servicios > 1).forEach((sus) => {
    recalcularSuscriptor(sus);
  });
};

const revisarPuedeAsignarCantidad = () => {
  cantidadAsignable.value = !infoServicio.value.mensual && !esTransporte.value;
};

// Watchers
watch(
  () => infoServicio.value.mensual,
  () => {
    revisarPuedeAsignarCantidad();
  },
);

watch(
  () => infoServicio.value.modalidad_id,
  () => {
    revisarPuedeAsignarCantidad();
    recalcularSuscriptores(suscriptoresValores.value);
  },
);

watch(
  cantidadAsignable,
  () => {
    if (!cantidadAsignable.value) {
      suscriptoresValores.value.forEach((sus) => { sus.numero_servicios = 1; });
    }
  },
);

// Hay que hacer este watch de los keys porque no se puede hacer el watch del objeto completo,
// no nos avisa si cambian los keys y si hacemos el deep se mete en bucle infinito
watch(
  keysSuscriptores,
  () => {
    // Nos aseguramos de asignarle el nombre de grupo para que luego sea más fácil todo
    suscriptoresValores.value.forEach((sus) => { sus.grupo = nombreGrupo(sus); });
    recalcularSuscriptores(suscriptoresValores.value);
  },
);
</script>

<template>
  <div class="card bg-secondary m-t">
    <div class="card-header border-bottom-cifra">
      {{ $I18n.t('suscriptores') }}
    </div>
    <div class="card-body">
      <table
        ref="tablaSuscriptores"
        class="table table-striped cifra-table cifra-table-gris table-form"
      >
        <thead>
          <tr>
            <th class="clickable" @click="ordenar('nombreCompleto')">
              <i class="fa fa-sort"></i> {{ $I18n.t('Nombre') }}
            </th>
            <th class="clickable" @click="ordenar('grupo')">
              <i class="fa fa-sort"></i> {{ $I18n.t('Grupo') }}
            </th>
            <th>{{ $I18n.t('Descuentos') }}</th>
            <th v-if="esTransporte">
              <button
                type="button"
                class="btn btn-xs btn-default as-check"
                @click="marcarTodoTransporte"
              >
                <i class="far fa-check-square"></i>
              </button>
              {{ esTransporteEventuales ? $I18n.t('eventuales') : $I18n.t('Turnos') }}
            </th>
            <th v-if="cantidadAsignable">
              {{ $I18n.t('Cantidad') }}
            </th>
            <th class="input-group-numeric-fixed">
              {{ $I18n.t('importe') }}
            </th>
            <th class="col-icon-1">
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="suscriptor in suscriptoresOdenados" :key="suscriptor.id">
            <td>{{ suscriptor.nombreCompleto }}</td>
            <td>{{ suscriptor.grupo }}</td>
            <td>
              <ul class="list-unstyled">
                <li v-for="descuento in suscriptor.descuentos" :key="descuento.id">
                  <span class="badge">
                    {{ descuentoDisplay(descuento) }}
                  </span> {{ descuento.nombre }}
                </li>
              </ul>
            </td>
            <td v-if="cantidadAsignable">
              <SelectorNumerico
                v-model.number="suscriptor.numero_servicios"
                class="input-3-digitos"
                size="sm"
                :min="1"
                :max="999"
                @update:model-value="recalcularSuscriptor(suscriptor)"
              >
              </SelectorNumerico>
            </td>
            <td v-if="esTransporte">
              <ul v-if="esTransporteEventuales" class="list-unstyled lista-turnos">
                <li
                  v-for="eventual in suscriptor.transporte_eventuales"
                  :key="eventual.id"
                >
                  <template v-if="!eventual['suscripcion?']">
                    <InputCheckboxBoolean
                      v-model="eventual.seleccionada"
                      class="check-transporte"
                      @update:model-value="actualizarNumeroServiciosEventuales(suscriptor)"
                    ></InputCheckboxBoolean>
                    {{ dateToLocaleDateString(eventual.fecha) }}
                    {{ eventual.turno.ruta.nombre }}
                    {{ eventual.turno.nombre }}
                  </template>
                </li>
              </ul>
              <ul v-else class="list-unstyled lista-turnos">
                <li
                  v-for="inscripcion in suscriptor.transporte_inscripciones"
                  :key="inscripcion.id"
                >
                  <InputCheckboxBoolean
                    v-if="!inscripcion['suscripcion?']"
                    v-model="inscripcion.seleccionada"
                    class="check-transporte"
                  ></InputCheckboxBoolean>
                  <span
                    v-else
                    data-toggle="title"
                    :title="$I18n.t('tiene_suscripcion_asociada')"
                    class="icono-suscripcion"
                  >
                    <i class="fa fa-dot-circle"></i>
                  </span>
                  {{ inscripcion.turno.ruta.nombre }}
                  {{ inscripcion.turno.nombre }}
                  {{ dateToTimeString(inscripcion.turno.hora) }}
                </li>
              </ul>
            </td>
            <td>
              <div class="input-group input-group-sm input-group-numeric-fixed">
                <input-numerico
                  v-model.number="suscriptor.importe_final"
                  class="form-control text-right"
                  configuracion="precio"
                  :disabled="!suscriptor.importe_manual"
                >
                </input-numerico>
                <span class="input-group-btn">
                  <button
                    class="btn"
                    type="button"
                    :class="{
                      'btn-default': !suscriptor.importe_manual,
                      'btn-white': suscriptor.importe_manual
                    }"
                    :title="suscriptor.importe_manual
                      ? 'Cambiar a cálculo automático'
                      : 'Cambiar a importe manual'"
                    @click="cambiarImporteManual(suscriptor)"
                  >
                    <span
                      class="fa"
                      :class="{
                        'fa-pencil': !suscriptor.importe_manual,
                        'fa-hand-point-left': suscriptor.importe_manual
                      }"
                    >
                    </span>
                  </button>
                </span>
              </div>
            </td>
            <td>
              <a :title="$I18n.t('Eliminar')" @click.prevent="eliminarSuscriptor(suscriptor.id)">
                <i class="fa fa-trash"></i>
              </a>
            </td>
          </tr>
        </tbody>
        <tfoot class="text-bold">
          <tr>
            <td :colspan="cantidadAsignable ? 4 : 3">
              <span class="badge badge-default text-16x">
                <i class="fa fa-users"></i> {{ numeroSuscriptores }}
              </span>
            </td>
            <td colspan="2">
              <div class="label label-success block text-16x">
                {{ totalImporteSuscriptores }}
              </div>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<style scoped>
  .icono-suscripcion {
    display: inline-block;
    width: 30px;
  }
</style>
